import React, { useCallback, useEffect, useState } from 'react';
import { FileError } from 'react-dropzone';

import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { LMN_UPLOAD_STORE_KEY, LMNPopupMode, LMNPopupStep } from '@/modules/MyDocuments/myDocuments.types';
import useLettersOfMedicalNecessityPopupStore from '@/modules/MyDocuments/stores/useLettersOfMedicalNecessityPopup.store';

import AssociateWithClaimsPopup from './AssociateWithClaimsPopup/AssociateWithClaimsPopup';
import ClaimsReviewPopup from './ClaimsReviewPopup/ClaimsReviewPopup';
import DocumentPreviewPopup from './DocumentPreviewPopup/DocumentPreviewPopup';
import LettersOfMedicalNecessityErrorPopup from './LettersOfMedicalNecessityErrorPopup/LettersOfMedicalNecessityErrorPopup';

interface Props {
  mode: LMNPopupMode;
  onSetVisible: (value: boolean) => void;
  onSuccess?: (value: string) => void;
  onEdit?: () => void;
  onDelete?: (lmnId: string) => void;
  itemId?: string;
  isMobile?: boolean;
  createForClaimId?: string;
  disabledClaimSelect?: boolean;
  disableResetDocumentStore?: boolean;
}

const LettersOfMedicalNecessityPopup: React.FC<Props> = ({
  mode,
  itemId,
  onEdit,
  onSetVisible,
  onSuccess,
  onDelete,
  isMobile,
  createForClaimId,
  disabledClaimSelect,
  disableResetDocumentStore,
}) => {
  const [step, setStep] = useState(LMNPopupStep.DOCUMENT_PREVIEW);
  const [isError, setIsError] = useState(false);
  const { handleReset: handleResetDocumentStore } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);
  const { sourceState, handleReset: handleResetLMNPopupStore } = useLettersOfMedicalNecessityPopupStore();

  const [isFileUploadErrorModalVisible, setIsFileUploadErrorModalVisible] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<FileError | undefined>();

  const onFileUploadError = useCallback((error?: FileError) => {
    setFileUploadError(error);
    setIsFileUploadErrorModalVisible(true);
  }, []);

  const handleUploadAnotherDocument = useCallback(() => {
    setStep(LMNPopupStep.DOCUMENT_PREVIEW);
    setFileUploadError(undefined);
    setIsFileUploadErrorModalVisible(false);
  }, []);

  const handleCloseFileUploadError = useCallback(() => {
    setFileUploadError(undefined);
    setIsFileUploadErrorModalVisible(false);
  }, []);

  useEffect(() => {
    return () => {
      if (!disableResetDocumentStore) {
        handleResetDocumentStore();
      }
      handleResetLMNPopupStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {step === LMNPopupStep.DOCUMENT_PREVIEW && (
        <DocumentPreviewPopup
          mode={mode}
          onSetVisible={onSetVisible}
          setStep={setStep}
          setIsError={setIsError}
          onSuccess={onSuccess}
          itemId={itemId}
          onEdit={onEdit}
          isMobile={isMobile}
          onFileUploadError={onFileUploadError}
          createForClaimId={createForClaimId}
          disabledClaimSelect={disabledClaimSelect}
          onDelete={onDelete}
        />
      )}
      {step === LMNPopupStep.ASSOCIATE_WITH_CLAIMS && (
        <AssociateWithClaimsPopup
          onSetVisible={onSetVisible}
          setStep={setStep}
          setIsError={setIsError}
          onSuccess={onSuccess}
          onFileUploadError={onFileUploadError}
          isMobile={isMobile}
        />
      )}
      {step === LMNPopupStep.CLAIMS_REVIEW_POPUP && (
        <ClaimsReviewPopup
          onSetVisible={onSetVisible}
          onSuccess={onSuccess}
          onFileUploadError={onFileUploadError}
          isMobile={isMobile}
        />
      )}
      {isError && (
        <LettersOfMedicalNecessityErrorPopup
          onSetVisible={setIsError}
          onSubmit={sourceState?.onError}
        />
      )}
      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup
          fileError={fileUploadError}
          onClose={handleCloseFileUploadError}
          onUploadAnotherDocument={handleUploadAnotherDocument}
        />
      )}
    </>
  );
};

export default LettersOfMedicalNecessityPopup;
